import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { IUser } from "../interfaces/user.interface";
import UserService from "../services/UserService";

interface AuthContextType {
  isAuthLoading: boolean;
  token: string | null;
  isAuthenticated: boolean;
  user: IUser | null;
  userProfile: IUser | null;
  login: (token: string, refreshToken: string) => void;
  logout: () => void;
  refreshProfile: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: readonly string[]) => boolean;
  hasAllPermissions: (permissions: readonly string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem("Authorization");
  });
  const [refreshToken, setRefreshToken] = useState<string | null>(() => null);
  const [user, setUser] = useState<IUser | null>(null);
  const [userProfile, setUserProfile] = useState<IUser | null>(null);

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return !!localStorage.getItem("Authorization");
  });

  const [isAuthLoading, setAuthIsLoading] = useState(true);

  // Fetch user profile when authenticated
  const fetchUserProfile = async () => {
    try {
      const profile = await UserService.getMyProfile();
      setUser(profile);
      setUserProfile(profile);
      return profile;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      // If profile fetch fails, logout the user
      logout();
      throw error;
    }
  };

  // Permission checking functions
  const hasPermission = (permission: string): boolean => {
    if (!userProfile?.role?.privileges) return false;
    return userProfile.role.privileges.some((priv) => priv.name === permission);
  };

  const hasAnyPermission = (permissions: readonly string[]): boolean => {
    if (!userProfile?.role?.privileges) return false;
    return permissions.some((permission) => hasPermission(permission));
  };

  const hasAllPermissions = (permissions: readonly string[]): boolean => {
    if (!userProfile?.role?.privileges) return false;
    return permissions.every((permission) => hasPermission(permission));
  };

  const refreshProfile = async (): Promise<void> => {
    if (isAuthenticated) {
      await fetchUserProfile();
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem("Authorization");
      if (storedToken) {
        setToken(storedToken);
        setIsAuthenticated(true);
        // Fetch user profile after authentication is confirmed
        try {
          await fetchUserProfile();
        } catch (error) {
          console.error("Failed to fetch profile on initialization:", error);
        }
      }
      setAuthIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (authToken: string, refreshToken: string) => {
    localStorage.setItem("Authorization", authToken);
    localStorage.setItem("RefreshToken", refreshToken);
    setToken(authToken);
    setRefreshToken(refreshToken);
    setIsAuthenticated(true);

    // Fetch user profile immediately after login
    try {
      await fetchUserProfile();
    } catch (error) {
      console.error("Failed to fetch profile after login:", error);
    }
  };

  const logout = () => {
    console.log("logout from authContext");
    localStorage.removeItem("Authorization");
    localStorage.removeItem("RefreshToken");
    localStorage.removeItem("userEmail");
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setUserProfile(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthLoading,
        token,
        isAuthenticated,
        user,
        userProfile,
        login,
        logout,
        refreshProfile,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
